import {
  addLanguageToTour,
  updateLanguageOnTour,
  updateTranslationsOnTour,
  getAllAvailableLanguages,
  getTourTranslationsByLanguage,
  _getTourTranslationsByType,
  _updateTourTranslationByType,
  fetchPublicTourLanguagesApi,
  getDownloadCSVUrl,
  downloadTranslationCSV,
} from 'api/v2/translations';
import { setUpToDate } from './tourActions';
import { setTBError } from './errorsActions';
import { batch } from 'react-redux';

const localTypes = {
  tour: 'tour',
  button: 'buttons',
  stop: 'stops',
};

const setButtonUrl = (translation, tour, id) => {
  const button = tour.buttons?.filter(btn => btn.id === id)[0];
  if (translation.dropdownOpts?.length && button) {
    translation.dropdownOpts?.forEach((btn, index) => {
      const baseBtn = button.dropdownOpts.filter(base => base.id === btn.id)[0];
      btn.url = baseBtn?.url;
    });
  } else {
    translation.url = button?.url;
  }
};

export const getTourTranslations = (lang, tourId) => {
  return async (dispatch, getState) => {
    dispatch(setTourTranslations());
    dispatch(setLoading());
    try {
      const res = await getTourTranslationsByLanguage(lang, tourId);
      dispatch(setTourTranslations(res));
    } catch (error) {
      console.error(error);
      dispatch(
        setTBError(
          error.message || 'Something went wrong getting tour translations.'
        )
      );
    } finally {
      dispatch(setDone());
    }
  };
};

export const getTourTranslationsByType = (lang, tourId, type, id) => {
  return async (dispatch, getState) => {
    dispatch(setActiveTranslation());
    dispatch(setLoading());
    try {
      let localType = localTypes[type];

      const res = await _getTourTranslationsByType(lang, tourId, localType, id);
      const baseTour = getState().tours?.tours[tourId] || {};
      dispatch(setActiveTranslation(res, baseTour, type, id));
    } catch (error) {
      console.error(error);
      dispatch(
        setTBError(
          error.message || 'Something went wrong getting tour translations.'
        )
      );
    } finally {
      dispatch(setDone());
    }
  };
};

export const getAvailableLanguages = () => {
  return async (dispatch, getState) => {
    dispatch(setLoading());
    try {
      const res = await getAllAvailableLanguages();
      dispatch(setAvailableLanguages(res));
    } catch (error) {
      console.error(error);
      dispatch(
        setTBError(
          error.message || 'Something went wrong getting tour languages.'
        )
      );
    } finally {
      dispatch(setDone());
    }
  };
};

export const addLanguage = (lang, tourId, langObj) => {
  return async (dispatch, getState) => {
    dispatch(setTourTranslations());
    dispatch(setLoading());
    try {
      const translations = await addLanguageToTour(lang, tourId);
      dispatch(setTourTranslations(translations));
      dispatch(updateLanguagesTour(langObj, tourId));
    } catch (error) {
      console.error(error);
      dispatch(
        setTBError(error.message || 'Something went wrong adding the language.')
      );
    } finally {
      dispatch(setDone());
    }
  };
};

export const updateTourLanguages = (langs, tourId) => {
  return async (dispatch, getState) => {
    dispatch(setSavingLanguage());
    try {
      await updateLanguageOnTour(langs, tourId);
      dispatch(updateLanguagesTour(langs, tourId));
    } catch (error) {
      console.error(error);
      dispatch(
        setTBError(error.message || 'Something went wrong updating languages.')
      );
    } finally {
      dispatch(setDone());
    }
  };
};

export const updateTourTranslations = (translations, tourId, lang) => {
  return async (dispatch, getState) => {
    dispatch(setSavingTranslations());
    try {
      await updateTranslationsOnTour(translations, tourId, lang);
      batch(() => {
        dispatch(updateTranslationsTour(translations));
        dispatch(setUpToDate(tourId, false));
      });
    } catch (error) {
      console.error(error);
      dispatch(
        setTBError(
          error.message || 'Something went wrong updating the translation.'
        )
      );
    } finally {
      dispatch(setDone());
    }
  };
};

export const updateTourTranslationByType = (
  translation,
  tourId,
  lang,
  type,
  id
) => {
  return async (dispatch, getState) => {
    dispatch(setSavingTranslations());
    try {
      let localType = localTypes[type];

      await _updateTourTranslationByType(
        translation,
        tourId,
        localType,
        lang,
        id
      );
      batch(() => {
        dispatch(updateActiveTranslation(translation));
        dispatch(setUpToDate(tourId, false));
      });
    } catch (error) {
      console.error(error);
      dispatch(
        setTBError(
          error.message || 'Something went wrong updating the translation.'
        )
      );
    } finally {
      dispatch(setDone());
    }
  };
};

export const fetchPublicTourLanguages = tourId => {
  return async dispatch => {
    try {
      const languages = await fetchPublicTourLanguagesApi(tourId);
      dispatch(savePublicTourLang(languages));
    } catch (e) {
      console.error(e);
    }
  };
};

export const startTranslationCSVDownload = tourId => {
  const aid = localStorage.getItem('aid');
  return async dispatch => {
    dispatch(setCSVStatus('saving'));
    try {
      const url = await getDownloadCSVUrl(aid, tourId);
      await downloadTranslationCSV(url);
      dispatch(setCSVStatus('ok'));
    } catch (e) {
      console.error(e);
      dispatch(setCSVStatus('error'));
    }
  };
};

export const setCSVStatus = (status = 'ok') => {
  return {
    type: 'TRANSLATIONS_CSV_STATUS',
    status,
  };
};

export const savePublicTourLang = languages => {
  return {
    type: 'SET_PUBLIC_TOUR_LANGUAGES',
    languages,
  };
};

export const setTourTranslations = (translations = {}) => {
  return {
    type: 'SET_TOUR_TRANSLATIONS',
    translations,
  };
};

export const setActiveTranslation = (
  translation = {},
  baseTour,
  translationType,
  id
) => {
  if (translationType === 'button') {
    setButtonUrl(translation, baseTour, id);
  }
  return {
    type: 'SET_ACTIVE_TRANSLATION',
    translation,
  };
};

export const setAvailableLanguages = languages => {
  return {
    type: 'SET_AVAILABLE_LANGUAGES',
    languages,
  };
};

const updateLanguagesTour = (language, tourId) => {
  return {
    type: 'UPDATE_TOUR_LANGUAGES',
    tourId,
    language,
  };
};

const updateTranslationsTour = translations => {
  return {
    type: 'UPDATE_TOUR_TRANSLATIONS',
    translations,
  };
};

const updateActiveTranslation = translation => {
  return {
    type: 'UPDATE_ACTIVE_TRANSLATION',
    translation,
  };
};

export const setLoading = () => {
  return {
    type: 'TRANSLATIONS_STATUS',
    status: 'loading',
    errorMessage: '',
  };
};

export const setSavingTranslations = () => {
  return {
    type: 'TRANSLATIONS_STATUS',
    status: 'saving_translation',
    errorMessage: '',
  };
};

export const setSavingLanguage = () => {
  return {
    type: 'TRANSLATIONS_STATUS',
    status: 'saving_language',
    errorMessage: '',
  };
};

export const setDone = () => {
  return {
    type: 'TRANSLATIONS_STATUS',
    status: 'ok',
    errorMessage: '',
  };
};
